import React, { useState, useEffect } from "react";
import axios from "axios";
import "../../Styles/ClassAttendance.css";
import Loader from "../../Components/Loader";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function ClassAttendance() {
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [facultyAttendanceStatus, setFacultyAttendanceStatus] = useState(null);

  // Fetch faculty's own attendance status for today
  useEffect(() => {
    const checkFacultyAttendance = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        if (!token) {
          console.error("No authentication token found");
          return;
        }

        const today = new Date();
        const formattedDate = today.toISOString().split("T")[0]; // YYYY-MM-DD format

        const response = await axios.get(
          "https://api.uniums.com/api/faculty/check-faculty-attendance",
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { date: formattedDate }
          }
        );

        if (response.data && response.data.status) {
          setFacultyAttendanceStatus(response.data.status);
          
          // If faculty is marked as absent, show a toast
          if (response.data.status === "Absent") {
            toast.error("You are marked as absent today. You cannot mark student attendance.");
          }
        } else {
          // If no attendance record found for today
          setFacultyAttendanceStatus("No Record");
          toast.warning("Your attendance for today has not been marked yet.");
        }
      } catch (error) {
        console.error("Error checking faculty attendance:", error);
        toast.error("Failed to verify your attendance status");
        setFacultyAttendanceStatus("Error");
      } finally {
        setLoading(false);
      }
    };

    checkFacultyAttendance();
  }, []);

  // Fetch classes and subjects from the API
  useEffect(() => {
    const fetchClassesAndSubjects = async () => {
      try {
        // Skip if faculty is absent
        if (facultyAttendanceStatus === "Absent") {
          return;
        }
        
        const token = localStorage.getItem("authToken");
        if (!token) {
          console.error("No authentication token found");
          return;
        }
        setLoading(true);

        const response = await axios.get(
          "https://api.uniums.com/api/faculty/faculty-classes-subjects",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data && response.data.data) {
          const { classes, subjects } = response.data.data;
          setClasses(classes);
          setSubjects(subjects);
        } else {
          console.error("Unexpected API response format:", response.data);
        }
      } catch (error) {
        console.error("Error fetching classes and subjects:", error);
        toast.error("Error fetching classes and subjects!");
      } finally {
        setLoading(false);
      }
    };

    // Only fetch classes and subjects if faculty attendance status is known
    if (facultyAttendanceStatus !== null) {
      fetchClassesAndSubjects();
    }
  }, [facultyAttendanceStatus]);

  // Clear attendance data when class or subject changes
  useEffect(() => {
    // Skip if faculty is absent
    if (facultyAttendanceStatus === "Absent") {
      return;
    }
    
    // Clear attendance data when selections change
    setAttendanceData([]);
    
    // Only fetch data if both class and subject are selected
    if (selectedClass && selectedSubject) {
      checkForExistingAttendance();
    }
  }, [selectedClass, selectedSubject, facultyAttendanceStatus]);

  // Check if there's existing attendance data for today, otherwise fetch students
  const checkForExistingAttendance = async () => {
    // Skip if faculty is absent
    if (facultyAttendanceStatus === "Absent") {
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.error("No authentication token found");
        return;
      }
      
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1;
      const currentDate = today.getDate();
      
      const response = await axios.get(
        `https://api.uniums.com/api/faculty/fetch-attendance`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            selectedClass,
            selectedSubject,
            selectedMonth: currentMonth,
            selectedDate: currentDate,
            year: currentYear
          },
        }
      );

      if (response.data && response.data.data && response.data.data.length > 0) {
        // If we have attendance data for today, use it
        console.log("Found existing attendance data:", response.data.data);
        setAttendanceData(response.data.data);
      } else {
        // If no attendance data for today, fetch students to mark fresh attendance
        console.log("No existing attendance found, fetching students...");
        fetchStudentsForClass();
      }
    } catch (error) {
      console.error("Error checking for existing attendance:", error);
      // If error checking for attendance, try to fetch students
      fetchStudentsForClass();
    } finally {
      setLoading(false);
    }
  };

  // Fetch students for the selected class and subject
  const fetchStudentsForClass = async () => {
    // Skip if faculty is absent
    if (facultyAttendanceStatus === "Absent") {
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.error("No authentication token found");
        return;
      }

      console.log(`Fetching students for Class: ${selectedClass}, Subject: ${selectedSubject}`);
      
      const response = await axios.get(
        `https://api.uniums.com/api/faculty/faculty-students`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { classId: selectedClass, subjectId: selectedSubject },
        }
      );

      if (response.data && response.data.data && response.data.data.length > 0) {
        console.log("Students fetched successfully:", response.data.data);
        
        // Filter students based on selectedClass and selectedSubject
        const filteredStudents = response.data.data.filter(student => 
          student.selectedClasses.includes(selectedClass) && 
          student.selectedSubjects.includes(selectedSubject)
        );
        
        console.log("Filtered students:", filteredStudents);
        
        if (filteredStudents.length > 0) {
          setAttendanceData(filteredStudents);
        } else {
          console.log("No students match the selected class and subject");
          toast.warning("No students found for this class and subject combination");
          setAttendanceData([]);
        }
      } else {
        console.error("No students data available");
        toast.error("No students data available!");
        setAttendanceData([]);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error("Error fetching students!");
    } finally {
      setLoading(false);
    }
  };

  // Handle status change in attendance table
  const handleStatusChange = (id, status) => {
    // Skip if faculty is absent
    if (facultyAttendanceStatus === "Absent") {
      return;
    }
    
    setAttendanceData((prevData) =>
      prevData.map((student) =>
        student._id === id ? { ...student, status } : student
      )
    );
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Skip if faculty is absent
    if (facultyAttendanceStatus === "Absent") {
      toast.error("You cannot submit attendance as you are marked absent today");
      setShowModal(false);
      return;
    }
    
    try {
      setSubmitLoading(true);
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.error("No authentication token found");
        return;
      }

      // Ensure you're submitting the latest attendance data
      const attendanceDataToSubmit = attendanceData
        .filter(student => student.status)  // Exclude students without a status
        .map((student) => ({
          _id: student._id,
          name: student.name,
          employeeCode: student.employeeCode,
          status: student.status,
        }));

      if (attendanceDataToSubmit.length === 0) {
        toast.warning("Please mark attendance for at least one student before submitting");
        setSubmitLoading(false);
        return;
      }

      const response = await axios.post(
        "https://api.uniums.com/api/faculty/student-save-attendance",
        {
          selectedClass,
          selectedSubject,
          attendanceData: attendanceDataToSubmit,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success("Attendance data submitted successfully!");
        setShowModal(false);
      }
    } catch (error) {
      console.error("Error submitting attendance:", error);
      toast.error("Error submitting attendance data!");
    } finally {
      setSubmitLoading(false); 
    }
  };

  // Render the attendance status notice
  const renderAttendanceStatusNotice = () => {
    if (facultyAttendanceStatus === "Absent") {
      return (
        <div className="attendance-status-notice absent">
          You are  as absent today. You cannot mark student attendance.
        </div>
      );
    } else if (facultyAttendanceStatus === "Present") {
      return (
        <div className="attendance-status-notice present">
          {/* You are marked as present today. You can mark student attendance. */}
        </div>
      );
    } else if (facultyAttendanceStatus === "No Record") {
      return (
        <div className="attendance-status-notice warning">
          Your attendance for today has not been marked yet. Please contact the admin.
        </div>
      );
    }
    return null;
  };

  return (
    <div className="class-attendance">
      <h1>Mark Attendance</h1>
      
      {/* Faculty's own attendance status notice */}
      {renderAttendanceStatusNotice()}

      {/* Dropdowns for Class and Subject - Disabled if faculty is absent */}
      <div className="dropdown-container">
        <div className="dropdown">
          <label htmlFor="class">Select Class:</label>
          <select
            id="class"
            className="class-dropdown"
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            disabled={facultyAttendanceStatus === "Absent"}
          >
            <option value="">-- Select Class --</option>
            {classes.map((cls, index) => (
              <option key={index} value={cls}>
                {cls}
              </option>
            ))}
          </select>
        </div>

        <div className="dropdown">
          <label htmlFor="subject">Select Subject:</label>
          <select
            id="subject"
            className="class-dropdown"
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
            disabled={facultyAttendanceStatus === "Absent"}
          >
            <option value="">-- Select Subject --</option>
            {subjects.map((subject, index) => (
              <option key={index} value={subject}>
                {subject}
              </option>
            ))}
          </select>
        </div>
      </div>
      {loading && <Loader />}

      {/* Attendance Table or Message */}
      {!loading && (
        <div className="attendance-table-container">
          {facultyAttendanceStatus === "Absent" ? (
            <p className="error">You cannot mark student attendance as you are absent today.</p>
          ) : selectedClass === "" || selectedSubject === "" ? (
            <p className="error">Please select a class and subject to mark the attendance.</p>
          ) : attendanceData.length === 0 ? (
            <p className="error">No attendance data available for the selected class and subject.</p>
          ) : (
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Sl. No.</th>
                  <th>Name</th>
                  <th>User Code</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {attendanceData.map((student, index) => (
                  <tr key={student._id || `${student._id}-${index}`}>
                    <td>{index + 1}</td>
                    <td>{student.name}</td>
                    <td>{student.employeeCode}</td>
                    <td>
                      <select
                        value={student.status || ""}
                        onChange={(e) => handleStatusChange(student._id, e.target.value)}
                        className="student_status_dropdown"
                        disabled={facultyAttendanceStatus === "Absent"}
                      >
                        <option value="">-- Select Status --</option>
                        <option value="Present" className="present">Present</option>
                        <option value="Absent" className="absent">Absent</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Submit Button - Disabled if faculty is absent */}
      <button 
        className="submit_button" 
        onClick={() => setShowModal(true)}
        disabled={!selectedClass || !selectedSubject || attendanceData.length === 0 || facultyAttendanceStatus === "Absent"}
      >
        Submit Attendance
      </button>

      {/* Confirmation Modal */}
      {showModal && (
        <div className="modal_overlay_new">
          <div className="modal_s">
            <h3 className="red_text">Confirmation</h3>
            <p>Are you sure you want to submit the attendance data?</p>
            <div className="modal_actions">
              <button className="modal_cancel_s" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button 
                className="modal_submit_s" 
                onClick={handleSubmit}
                disabled={submitLoading || facultyAttendanceStatus === "Absent"}
              >
                {submitLoading ? "Processing..." : "Submit"}
              </button>
            </div>
            {submitLoading && <div className="modal_loader"><Loader /></div>}
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
}

export default ClassAttendance;